import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import {
  isWhiteboardModeAtom,
  patientConsentAtom,
} from "../../session/messages/state";
import { resolveCorrectConsentState } from "../../session/messages/utils";
import type {
  DataChannelAppState,
  DataChannelAppStateMessage,
  DataChannelAppStateProperty,
} from "../../video-sdk";
import {
  AudioRecordingState,
  ConsentStatus,
  DataChannelUpdateStatus,
  getNewestDataChannelState,
  recordingStateAtom,
} from "../../video-sdk";
import { useClientTranscriptionResponseToastCallback } from "../session-recording/useClientTranscriptionResponseToastCallback";
import { useStartRecordingCallback } from "../session-recording/useStartRecordingCallback";
import { useStopRecordingCallback } from "../session-recording/useStopRecordingCallback";
import { useSendProviderParticipantStateCallback } from "./useSendProviderParticipantStateCallback";

function useHandleClientConsentCallback() {
  const getUserPatientConsent = useAtomCallback(
    useCallback((get) => get(patientConsentAtom), []),
  );
  const sendParticipantState = useSendProviderParticipantStateCallback();
  const popClientResponseTranscriptionToast =
    useClientTranscriptionResponseToastCallback();

  const setPatientConsent = useSetAtom(patientConsentAtom);

  return useCallback(
    async (receivedParticipantState: DataChannelAppState) => {
      const currentConsentState = getUserPatientConsent();
      const receivedConsentState = receivedParticipantState.patient.consent;
      const [correctConsentState, comparisonResult] =
        resolveCorrectConsentState({
          receivedState: receivedConsentState,
          userState: currentConsentState,
        });

      if (comparisonResult === DataChannelUpdateStatus.NO_UPDATE_NEEDED) {
        return;
      }

      setPatientConsent(correctConsentState);

      const receivedNewConsentValue =
        comparisonResult ===
        DataChannelUpdateStatus.VALUE_AND_LAST_UPDATED_CHANGED;

      if (!receivedNewConsentValue) {
        return;
      }
      /** If the patient has newly consented,
       * record the consent and tell the provider
       */
      if (correctConsentState.value === ConsentStatus.OPTED_IN) {
        popClientResponseTranscriptionToast(true);
      }
      /** If the patient has declined to consent,
       *  tell the provider
       */
      if (correctConsentState.value === ConsentStatus.SOFT_NO) {
        popClientResponseTranscriptionToast(false);
      }
      sendParticipantState();
    },
    [
      getUserPatientConsent,
      setPatientConsent,
      sendParticipantState,
      popClientResponseTranscriptionToast,
    ],
  );
}

function useHandleRecordingUpdateCallback() {
  const startRecording = useStartRecordingCallback();
  const stopRecording = useStopRecordingCallback();
  const getRecordingState = useAtomCallback(
    useCallback((get) => get(recordingStateAtom), []),
  );
  return useCallback(
    async (receivedState: DataChannelAppState) => {
      const currentState = getRecordingState();
      // TODO: CARED-2179 We currently still check both `recordingState` and
      // `isRecording` to ensure compatibility with data track messages sent
      // from clients on an older FE version that may only have the latter.
      const receivedRecordingState =
        receivedState.recordingState ??
        ({
          value: receivedState.isRecording.value
            ? AudioRecordingState.RECORDING
            : AudioRecordingState.NOT_RECORDING,
          lastUpdated: receivedState.isRecording.lastUpdated,
        } satisfies DataChannelAppStateProperty<AudioRecordingState>);
      const [newestRecordingState, comparisonResult] =
        getNewestDataChannelState<AudioRecordingState>({
          receivedState: receivedRecordingState,
          userState: currentState,
        });

      // Return early if no update is needed
      if (comparisonResult === DataChannelUpdateStatus.NO_UPDATE_NEEDED) {
        return;
      }

      if (newestRecordingState.value === AudioRecordingState.RECORDING) {
        await startRecording({ lastUpdated: newestRecordingState.lastUpdated });
      } else {
        await stopRecording({ lastUpdated: newestRecordingState.lastUpdated });
      }
    },
    [startRecording, stopRecording, getRecordingState],
  );
}

function useHandleIsWhiteboardModeUpdateCallback() {
  const getCurrentIsWhiteboardMode = useAtomCallback(
    useCallback((get) => get(isWhiteboardModeAtom), []),
  );
  const setIsWhiteboardMode = useSetAtom(isWhiteboardModeAtom);

  return useCallback(
    (receivedState: DataChannelAppState) => {
      const [newestIsWhiteboardMode, comparisonResult] =
        getNewestDataChannelState({
          receivedState: receivedState.isWhiteboardMode,
          userState: getCurrentIsWhiteboardMode(),
        });

      if (comparisonResult === DataChannelUpdateStatus.NO_UPDATE_NEEDED) {
        return;
      }
      setIsWhiteboardMode(newestIsWhiteboardMode);
    },
    [getCurrentIsWhiteboardMode, setIsWhiteboardMode],
  );
}

export function useUpdateDataChannelAppStateCallback() {
  const handleRecordingUpdate = useHandleRecordingUpdateCallback();
  const handleClientConsentUpdate = useHandleClientConsentCallback();
  const handleIsWhiteboardModeUpdate =
    useHandleIsWhiteboardModeUpdateCallback();

  return useCallback(
    async (message: DataChannelAppStateMessage) => {
      const { participantState } = message;
      handleIsWhiteboardModeUpdate(participantState);
      handleClientConsentUpdate(participantState);
      await handleRecordingUpdate(participantState);
    },
    [
      handleClientConsentUpdate,
      handleRecordingUpdate,
      handleIsWhiteboardModeUpdate,
    ],
  );
}
